// Copyright ©️ 2026 eVolve MEP, LLC

import './kanban.css';

import { useCallback, useMemo } from 'react';

import { useEvolvePatch, type WorkOrderId, type WorkOrderKanban, workOrderStateNames } from '@evolvemep/api-ts';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import { KanbanBoard } from 'components/Mantine/Kanban/KanbanBoard';
import type { CardConfigMapper } from 'components/Mantine/Kanban/types';
import type { EntityState } from 'components/Mantine/StatusEditor/types';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { hasLengthExactly } from 'helpers/helper-types';
import { NewWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';
import { WorkOrderKanbanCard } from 'modules/Shop/WorkOrders/WorkOrdersPage/WorkOrderKanbanCard';

import { PendingStatusRetirementBanner } from './PendingStatusRetirementBanner';
import type { WorkOrdersPageProps } from './WorkOrdersPage';

export const WorkOrdersKanbanView = ({ states }: WorkOrdersPageProps) => {
  const { getLabelAndColor } = useCustomStatuses('Work Order');
  const { facilityIds, projectIds, projects } = useGlobalFilters({
    facilities: true,
    projects: true,
  });

  const swimLanes = useMemo(
    () =>
      states
        .filter(
          (s) =>
            s.name !== 'Completed' &&
            // Safety check, just in case the BE returns states we don't support yet
            workOrderStateNames.includes(s.name),
        )
        .toSorted((a, b) => workOrderStateNames.indexOf(a.name) - workOrderStateNames.indexOf(b.name)),
    [states],
  );
  const cardConfigMapper: CardConfigMapper<EntityState<'Work Order'>, 'shop/workOrder/kanban'> = useCallback(
    (state) => ({
      requestConfig: {
        url: 'shop/workOrder/kanban',
        params: {
          workOrderStateId: state.stateId,
          facilityIds,
          projectIds,
        },
      },
    }),
    [facilityIds, projectIds],
  );
  const { apiCall: reorderWorkOrderApiCall, loading: mutationRunning } = useEvolvePatch(
    'shop/workOrder/:workOrderId/prioritize',
  );
  const onDragEnd = async (workOrderId: WorkOrderId, after?: WorkOrderId) => {
    await reorderWorkOrderApiCall(
      {},
      {
        url: `shop/workOrder/${workOrderId}/prioritize`,
        params: { after },
      },
    );
  };

  return (
    <>
      <BasePageHeader
        title="Work Orders"
        topRightComponent={
          <NewWorkOrderModal defaultProject={hasLengthExactly(projects, 1) ? projects[0] : undefined} />
        }
      />

      <PendingStatusRetirementBanner />

      <KanbanBoard<never, EntityState<'Work Order'>, 'stateId', 'shop/workOrder/kanban', WorkOrderKanban, 'workOrderId'>
        lanes={swimLanes}
        laneIdKey="stateId"
        getLaneLabel={(state) => getLabelAndColor(state.name).label}
        getLaneColor={(state) => getLabelAndColor(state.name).color}
        cardIdKey="workOrderId"
        cardConfigMapper={cardConfigMapper}
        draggingTooltip="Drag card within column to set priority"
        onDragEnd={onDragEnd}
      >
        {(workOrder, index) => (
          <WorkOrderKanbanCard
            key={workOrder.workOrderId}
            workOrder={workOrder}
            index={index}
            draggable={!mutationRunning}
          />
        )}
      </KanbanBoard>
    </>
  );
};
